import { API_ENDPOINT, fetchWithAuth, parseApiResponse } from ".";
import type { MembershipTier } from "../types/pricingConfig";

export type PricingPeriodKey = "early_bird" | "standard" | "on_site";

export interface TicketSetPeriod {
  key: PricingPeriodKey;
  label: string;
  endsAt: string | null;
}

export interface TicketSetPeriodConfig {
  periods: TicketSetPeriod[];
}

export type TicketCategory =
  | "full_conference"
  | "single_day"
  | "add_on"
  | "extra_page"
  | "additional_paper"
  | "test"
  | null;

export interface TicketSetTypeInput {
  typeName: string;
  description?: string;
  category: TicketCategory;
  membershipTier: MembershipTier | null;
  itemOrderedLabel?: string;
  currency?: string;
  pricePence?: number;
  pricesByPeriod?: Partial<Record<PricingPeriodKey, number>>;
}

export interface TicketSetInput {
  /** Soft id = set name when loaded from API (no sets table). */
  id?: string | number;
  name: string;
  hasPricingPeriods: boolean;
  periodConfig?: TicketSetPeriodConfig | null;
  sortOrder?: number;
  types: TicketSetTypeInput[];
}

export interface AdminTicketSet {
  id: string;
  eventId: number;
  name: string;
  hasPricingPeriods: boolean;
  periodConfig: TicketSetPeriodConfig | null;
  sortOrder: number;
  ticketTypes: {
    id: number;
    name: string;
    typeName: string | null;
    ticketSetName?: string | null;
    pricePence: number;
    currency: string;
    category: string | null;
    membershipTier: string | null;
    pricingPeriod: string | null;
    itemOrderedLabel: string | null;
    status: string;
    salesStartAt?: string | null;
    salesEndAt?: string | null;
  }[];
}

export async function listEventTicketSets(
  eventId: number,
): Promise<AdminTicketSet[]> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${eventId}/ticket-sets`,
  );
  const data = await parseApiResponse<{ sets: AdminTicketSet[] }>(response);
  return data.sets;
}

export async function syncEventTicketSets(
  eventId: number,
  sets: TicketSetInput[],
): Promise<AdminTicketSet[]> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${eventId}/ticket-sets`,
    {
      method: "PUT",
      body: JSON.stringify({ sets }),
    },
  );
  const data = await parseApiResponse<{ sets: AdminTicketSet[] }>(response);
  return data.sets;
}

export function defaultPeriodConfig(eventStartIso: string): TicketSetPeriodConfig {
  const start = new Date(eventStartIso);
  const earlyBirdEnd = new Date(start);
  earlyBirdEnd.setDate(earlyBirdEnd.getDate() - 90);
  const standardEnd = new Date(start);
  standardEnd.setDate(standardEnd.getDate() - 30);
  return {
    periods: [
      {
        key: "early_bird",
        label: "Early Bird",
        endsAt: earlyBirdEnd.toISOString(),
      },
      {
        key: "standard",
        label: "Standard",
        endsAt: standardEnd.toISOString(),
      },
      { key: "on_site", label: "On-Site", endsAt: null },
    ],
  };
}

export function emptyTicketSet(_eventStartIso?: string): TicketSetInput {
  return {
    name: "",
    hasPricingPeriods: false,
    periodConfig: null,
    sortOrder: 0,
    types: [
      {
        typeName: "",
        category: null,
        membershipTier: null,
        currency: "usd",
        pricePence: 0,
      },
    ],
  };
}

/** Collapse API set + period ticket rows into editable admin draft. */
export function adminSetsToDrafts(sets: AdminTicketSet[]): TicketSetInput[] {
  return sets.map((set) => {
    const periodConfig =
      set.periodConfig &&
      typeof set.periodConfig === "object" &&
      Array.isArray((set.periodConfig as TicketSetPeriodConfig).periods)
        ? (set.periodConfig as TicketSetPeriodConfig)
        : null;

    const byType = new Map<
      string,
      {
        typeName: string;
        description?: string;
        category: TicketCategory;
        membershipTier: MembershipTier | null;
        itemOrderedLabel?: string;
        currency: string;
        pricePence?: number;
        pricesByPeriod?: Partial<Record<PricingPeriodKey, number>>;
      }
    >();

    for (const t of set.ticketTypes) {
      const key = `${t.typeName ?? t.name}::${t.category ?? ""}::${t.membershipTier ?? ""}`;
      if (set.hasPricingPeriods && t.pricingPeriod) {
        const existing = byType.get(key);
        const prices = existing?.pricesByPeriod ?? {};
        prices[t.pricingPeriod as PricingPeriodKey] = t.pricePence;
        byType.set(key, {
          typeName: t.typeName ?? t.name,
          category: (t.category as TicketCategory) ?? null,
          membershipTier: (t.membershipTier as MembershipTier) ?? null,
          itemOrderedLabel: t.itemOrderedLabel ?? undefined,
          currency: t.currency,
          pricesByPeriod: prices,
        });
      } else {
        byType.set(key, {
          typeName: t.typeName ?? t.name,
          category: (t.category as TicketCategory) ?? null,
          membershipTier: (t.membershipTier as MembershipTier) ?? null,
          itemOrderedLabel: t.itemOrderedLabel ?? undefined,
          currency: t.currency,
          pricePence: t.pricePence,
        });
      }
    }

    return {
      id: set.id,
      name: set.name,
      hasPricingPeriods: set.hasPricingPeriods,
      periodConfig,
      sortOrder: set.sortOrder,
      types: Array.from(byType.values()),
    };
  });
}
